/**
 * Board definition format – the compact, human-editable shape that is stored as JSON
 * (bundled boards in `@archon3d/content`, user boards from the editor).
 *
 * Coordinates: `x` grows to the east (right when viewed from the south edge), `y` grows to the
 * south. `y = 0` is the first row string. On a hex board the same rows are read as "odd-r" offset
 * coordinates: pointy-top hexes in horizontal rows, odd rows shifted half a cell to the east (see
 * `geometry.ts`). Cell strings use one character per cell:
 *
 * | Char | Meaning |
 * | --- | --- |
 * | `L` | permanently light |
 * | `D` | permanently dark |
 * | `~` | luminosity cycles between light and dark |
 * | `#` | void – not part of the board (used for non-rectangular boards) |
 */
export interface BoardDef {
  id: string;
  name: string;
  description?: string;
  /** Square cells with four neighbours (the default) or hexes with six. */
  grid?: 'square' | 'hex';
  width: number;
  height: number;
  /** One string per row, `height` rows of `width` characters. */
  cells: string[];
  /** `[x, y]` of each power point. */
  powerPoints: [number, number][];
  /** Walls between adjacent cells (sharing an edge), each as two `[x, y]` pairs. */
  walls?: [[number, number], [number, number]][];
  /**
   * The sides in seat order, two to six: at most four on a square board, six on a hex board. A
   * game with fewer players drops the highest-numbered factions (see `createGame`).
   */
  factions: FactionDef[];
  placements: PlacementDef[];
  /**
   * Where the luminosity cycle stands when a game begins. Left out, a game starts from one of
   * the two mirror-image middle phases, chosen by its seed, so that neither side owns the
   * opening rounds (see `initialLuminosity`); a board names a start when its design wants one.
   */
  luminosity?: LuminosityStart;
}

/** The classic cycle: black – blue – purple – green – cyan – white, and back. */
export const LUMINOSITY_STEPS = 6;

/** A point of the luminosity cycle: a phase and the direction it moves in. */
export interface LuminosityStart {
  /** 0 = darkest … `LUMINOSITY_STEPS − 1` = brightest. */
  phase: number;
  /** +1 towards the bright end, −1 towards the dark end. */
  direction: 1 | -1;
}

export interface FactionDef {
  id: string;
  name: string;
  /** Hex colour used for pieces and UI, e.g. `#f2d27a`. */
  color: string;
  /** Which luminosity strengthens this faction's pieces. */
  affinity: 'light' | 'dark' | 'neutral';
}

export interface PlacementDef {
  faction: string;
  /** Character id from the roster (see `@archon3d/content`). */
  character: string;
  x: number;
  y: number;
}

export type Luminosity = 'light' | 'dark' | 'cycling';
export type CellKind = 'floor' | 'void';

/** Expanded, validated cell used by rules, AI and rendering. */
export interface Cell {
  readonly x: number;
  readonly y: number;
  readonly kind: CellKind;
  /** `null` for void cells. */
  readonly luminosity: Luminosity | null;
  readonly powerPoint: boolean;
}

/** Expanded, validated board. Cells are stored row-major: index = y * width + x. */
export interface Board {
  readonly def: BoardDef;
  readonly width: number;
  readonly height: number;
  readonly cells: readonly Cell[];
}
